import React, {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as chatApi from '../services/chatApi';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
  /** Only set on user messages that failed to send */
  status?: 'error';
}

/* eslint-disable no-unused-vars */
export interface ChatContextValue {
  messages: ChatMessage[];
  sessionId: string | undefined;
  isOpen: boolean;
  isSending: boolean;
  escalated: boolean;
  hasBeenOpened: boolean;
  sendMessage: (text: string) => Promise<void>;
  retryMessage: (id: string) => Promise<void>;
  open: () => void;
  minimize: () => void;
}
/* eslint-enable no-unused-vars */

// ── Context ───────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export const ChatContext = createContext<ChatContextValue | null>(null);

const WELCOME: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hi, I'm Aria 👋 — your support assistant. Ask me anything about your vouchers and I'll do my best to help. If I can't, I'll connect you with our team.",
  createdAt: new Date(),
};

// ── Provider ──────────────────────────────────────────────────────────────────

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  // Use a ref so send callbacks always see the latest sessionId without
  // being recreated whenever it changes.
  const sessionIdRef = useRef<string | undefined>(undefined);
  const [sessionId, setSessionIdState] = useState<string | undefined>(undefined);

  function setSessionId(id: string) {
    sessionIdRef.current = id;
    setSessionIdState(id);
  }

  // Core send routine — identified by the message's id so status can be patched
  const doSend = useCallback(async (text: string, msgId: string) => {
    setIsSending(true);
    try {
      const resp = await chatApi.sendMessage(text, sessionIdRef.current);
      setSessionId(resp.sessionId);
      if (resp.escalated) setEscalated(true);

      setMessages((prev) => [
        ...prev.map((m) =>
          m.id === msgId ? { ...m, status: undefined } : m,
        ),
        {
          id: `a-${Date.now()}`,
          role: 'assistant' as const,
          content: resp.reply,
          createdAt: new Date(),
        },
      ]);
    } catch {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId ? { ...m, status: 'error' as const } : m,
        ),
      );
    } finally {
      setIsSending(false);
    }
  }, []); // stable — reads sessionId via ref, setters are stable

  const sendMessage = useCallback(
    async (text: string) => {
      const id = `u-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id, role: 'user', content: text, createdAt: new Date() },
      ]);
      await doSend(text, id);
    },
    [doSend],
  );

  const retryMessage = useCallback(
    async (id: string) => {
      const msg = messages.find((m) => m.id === id);
      if (!msg) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, status: undefined } : m,
        ),
      );
      await doSend(msg.content, id);
    },
    [messages, doSend],
  );

  const open = useCallback(() => {
    setIsOpen(true);
    setHasBeenOpened(true);
  }, []);

  const minimize = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo<ChatContextValue>(
    () => ({
      messages,
      sessionId,
      isOpen,
      isSending,
      escalated,
      hasBeenOpened,
      sendMessage,
      retryMessage,
      open,
      minimize,
    }),
    [
      messages,
      sessionId,
      isOpen,
      isSending,
      escalated,
      hasBeenOpened,
      sendMessage,
      retryMessage,
      open,
      minimize,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
